/**
 * Harness scenarios. `PPM.harness.setState({scenario})` drops the world into a named,
 * fully deterministic gameplay tableau so a shot has something real to photograph.
 *
 * Every scenario is a pure function of (seed, scenario name) — no wall clock, no incremental
 * state — so applying the same scenario twice produces byte-identical layout. The capture
 * pipeline calls setState twice (once before the settle steps, once after), which is exactly
 * why that has to be true.
 *
 * AXIS: content is laid along the street in front of the PLAYER once `vehicle` has landed.
 * Until then `ctx.player` is the null-object truck parked at the origin facing +Z while the
 * default camera looks down -Z, so we fall back to the camera's own view direction — that is
 * what `isFallback` exists for (ARCHITECTURE.md §4.10) and it self-removes.
 */

const LANDMARKS = [
  'Dépanneur Chez Momo', 'av. du Mont-Royal', 'rue Saint-Denis', 'ruelle Marie-Anne',
  'Parc La Fontaine', 'boul. Saint-Laurent', 'rue Rachel', 'Fruiterie Mile End',
  'Église Saint-Jean-Baptiste', 'rue Drolet', 'av. Duluth', 'Café Olimpico',
];

export const SCENARIOS = Object.freeze([
  'cruise-straight', 'repair-closeup', 'combo-moment', 'cone-slalom',
  'establish', 'result', 'night-street', 'none',
]);

/**
 * Work out the axis to lay content along: origin point and a unit direction in the xz plane.
 * `out` is reused — no allocation.
 */
export function scenarioAxis(ctx, out) {
  const p = ctx.player;
  let ox = 0, oz = 0, dx = 0, dz = 1;
  if (ctx.camera) ctx.camera.updateMatrixWorld(true);

  if (p && !p.isFallback && p.position && p.forward) {
    ox = p.position.x; oz = p.position.z;
    dx = p.forward.x; dz = p.forward.z;
  } else if (ctx.camera) {
    // Vehicle has not landed. Aim down the camera's own view axis so the shot is not empty.
    const m = ctx.camera.matrixWorld.elements;
    dx = -m[8]; dz = -m[10];
    ox = ctx.camera.position.x;
    oz = ctx.camera.position.z;
  }

  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-4) { dx = 0; dz = 1; } else { dx /= len; dz /= len; }

  out.ox = ox; out.oz = oz; out.dx = dx; out.dz = dz;
  out.px = -dz; out.pz = dx;
  return out;
}

/**
 * Lay a scenario. `gp` is the GameplaySystem — scenarios reach into it deliberately, they are
 * authoring tools for the shot set rather than part of the runtime loop.
 */
export function applyScenario(gp, name) {
  const ctx = gp.ctx;
  const T = gp.T;
  const S = T.scenario;
  const rng = gp.rng.fork('scenario:' + name);
  const axis = scenarioAxis(ctx, gp._axis);

  gp.clearField();

  switch (name) {
    case 'repair-closeup': return closeup(gp, rng, axis, S, T);
    case 'combo-moment': return comboMoment(gp, rng, axis, S, T);
    case 'cone-slalom': return coneSlalom(gp, rng, axis, S, T);
    case 'establish': return establish(gp, rng, axis, S, T);
    case 'result': return resultTableau(gp, rng, axis, S, T);
    case 'night-street': return nightStreet(gp, rng, axis, S, T);
    case 'none': return;
    case 'cruise-straight':
    default: return cruiseStraight(gp, rng, axis, S, T);
  }
}

// ------------------------------------------------------------------ helpers

/** Place a hole `along` metres down the axis, `across` metres to the side of the centreline. */
function place(gp, axis, along, across, rng, opts) {
  const x = axis.ox + axis.dx * along + axis.px * across;
  const z = axis.oz + axis.dz * along + axis.pz * across;
  const p = gp.potholes.spawn(x, z, rng, gp.weather, opts);
  if (!p) return null;
  p.name = LANDMARKS[rng.int(0, LANDMARKS.length)];
  gp.cones.ringPothole(p, rng);
  return p;
}

/** A hole that has already been repaired, sitting there as before/after evidence. */
function placeCured(gp, axis, along, across, rng, cureFrac) {
  const p = place(gp, axis, along, across, rng, { coneInHole: false, hasWater: false });
  if (!p) return null;
  gp.potholes.complete(p, 0.72, false);
  p.patchAge = gp.T.pothole.cureSeconds * cureFrac;
  if (cureFrac >= 1) { p.state = 'cured'; p.cured = true; }
  // Marker ring comes down once the patch is in; leave the guard cone.
  gp.retireRing(p);
  gp.cones.deploy(p, axis.px, axis.pz, rng);
  return p;
}

// ------------------------------------------------------------------ scenarios

/** Ordinary street: a run of holes ahead, cone rings, a slalom stretch, patches behind. */
function cruiseStraight(gp, rng, axis, S, T) {
  let along = S.nearClear + 4;
  for (let i = 0; i < 7; i++) {
    place(gp, axis, along, (i % 2 === 0 ? -1 : 1) * S.laneOffset * rng.range(0.7, 1.25), rng);
    along += rng.range(S.strideMin, S.strideMax);
  }
  placeCured(gp, axis, -12, S.laneOffset * 0.8, rng, 1);
  placeCured(gp, axis, -30, -S.laneOffset * 0.9, rng, 1);

  gp.cones.layRun(
    axis.ox + axis.dx * 46 + axis.px * -1.6, axis.oz + axis.dz * 46 + axis.pz * -1.6,
    axis.dx, axis.dz, 5, rng
  );
  gp.retargetNow();
}

/**
 * The hero repair. One comically large hole at the world origin, because the `pothole-fill`
 * shot camera is hard-pointed at (0.15, 0.05, 0.1). Session is bound and PINNED so the meter
 * holds still no matter how many steps the capture takes.
 */
function closeup(gp, rng, axis, S, T) {
  const x = axis.ox + axis.dx * S.closeupAhead;
  const z = axis.oz + axis.dz * S.closeupAhead;
  const p = gp.potholes.spawn(x, z, rng, gp.weather, {
    radius: S.closeupRadius,
    depth: S.closeupDepth,
    hasWater: true,
    coneInHole: false,
  });
  if (!p) return;
  p.name = 'av. du Mont-Royal';
  gp.cones.ringPothole(p, rng);

  /**
   * Rubric `pothole-fill` criterion 7 — cured patches in the SAME frame, so the fresh fill
   * reads as fresh by comparison. These only became possible once the hero hole shrank: at
   * the old radius the crater covered the ground they sit on. See TUNING.scenario.closeupCured.
   */
  for (let i = 0; i < S.closeupCured.length; i++) {
    const c = S.closeupCured[i];
    placeCured(gp, axis, S.closeupAhead + c.along, c.across, rng, c.cure);
  }

  // Something for the background to do.
  place(gp, axis, S.closeupAhead + 26, S.laneOffset, rng);
  place(gp, axis, S.closeupAhead + 49, -S.laneOffset, rng);

  // retargetNow first: it is what fills the near-hole list, and without that the very next
  // fixed step would see nothing in the work zone and immediately drop the session.
  gp.retargetNow();
  gp.forceTarget(p);
  gp.beginSession(p, true);
  gp.pinSession(0.62);
}

/** The loud payoff frame: a patch you just laid, a cone on it, and the next hole ahead. */
function comboMoment(gp, rng, axis, S, T) {
  // Everything sits past `nearClear`: the chase camera looks over the cab, so a patch nine
  // metres ahead is a patch nobody can see.
  const fresh = placeCured(gp, axis, S.nearClear + 3, S.laneOffset * 0.55, rng, 0.28);
  if (fresh) gp.lastCompleted = fresh;

  place(gp, axis, S.nearClear + 14, -S.laneOffset * 0.9, rng, { hasWater: true });
  place(gp, axis, S.nearClear + 37, S.laneOffset, rng);
  place(gp, axis, S.nearClear + 63, -S.laneOffset * 0.7, rng);
  placeCured(gp, axis, -16, -S.laneOffset, rng, 1);

  gp.cones.layRun(
    axis.ox + axis.dx * (S.nearClear + 21) + axis.px * 1.4,
    axis.oz + axis.dz * (S.nearClear + 21) + axis.pz * 1.4,
    axis.dx, axis.dz, 4, rng
  );
  gp.retargetNow();
  gp.announce('NID-DE-POULE COMBO', 'combo', 2200);
}

/**
 * A corridor of gates the truck is IN THE MIDDLE OF, with the chain already running.
 *
 * ROUND 7. Two things were wrong and only one of them was the corridor's fault.
 *
 *  1. Every gate cone was spawned at y = 0 while the road under this shot sits at
 *     y = 1.407 — the whole corridor was rendering INSIDE the road mesh. Fixed in
 *     ConeField.layRun; that is why the shot had "no slalom" for four rounds.
 *  2. The corridor started `slalomStart` metres AHEAD of the spawn pose on the assumption
 *     the truck drives straight down it. Traced, it does not: the capture driver saturates
 *     at steer = 1 from step 45 onward and the truck arrives at the shutter yawed 65° off
 *     the street at 23 km/h, so a corridor laid ahead of the spawn heading ends up 43° off
 *     the camera axis. We cannot fix the driver from here (harness owns `drive`), so the
 *     corridor now STRADDLES the truck: it starts behind the spawn point and runs well past
 *     it, which puts gate cones in the near field on both sides whatever the final heading.
 *     That is also what the critic asked for — "near cones enter the bottom edge of frame
 *     and sweep past the lens" — and rubric criterion 7's "overshoots the work zone at both
 *     ends".
 */
function coneSlalom(gp, rng, axis, S, T) {
  // ROUND 11: offset across the street. The corridor was previously laid on the truck's own
  // line, so the near gates were occluded by the truck body — see ConeField.layRun.
  const laid = gp.cones.layRun(
    axis.ox + axis.dx * S.slalomStart, axis.oz + axis.dz * S.slalomStart,
    axis.dx, axis.dz, S.slalomGates, rng, S.slalomAcross
  );
  /**
   * Rubric criterion 6 wants at least one cone knocked over in any group of eight or more.
   * It used to knock the LAST two gates, which are 90 m away and a few pixels tall — the
   * criterion is graded by looking, so the felled cone has to be one you can see. Knock one
   * side of a gate in the near third of the run instead.
   */
  const knockGate = Math.min(laid - 1, S.slalomKnockGate);
  const live = gp.cones.live();
  for (let i = 0; i < live.length; i++) {
    const c = live[i];
    if (c.gateId === knockGate && c.gateSide > 0) {
      c.tilt = Math.PI * 0.5;
      c.state = 'knocked';
      c.yaw = rng.range(0, Math.PI * 2);
    }
  }

  // The surplus. Rubric criterion 7 is graded by counting cones against the work being
  // marked, and a bare corridor of nine gates does not read as excessive to anybody who has
  // driven in Montréal. These line the kerbs either side of the corridor.
  gp.cones.layScatter(
    axis.ox + axis.dx * S.slalomScatterStart, axis.oz + axis.dz * S.slalomScatterStart,
    axis.dx, axis.dz, S.slalomScatterCount, S.slalomScatterAcross, S.slalomScatterStep, rng
  );

  /**
   * The work actually being marked. The corridor hole used to sit 1.35 lanes off the
   * centreline, and since `retargetNow` picks the nearest hole that is what the HUD's
   * waypoint chevron aimed at — a critic reported the arrow "points at a rooftop", which it
   * did, because the target was off to the left behind the frontage. Putting it ON the
   * corridor centreline well down the run and force-targeting it makes the chevron sit over
   * the corridor's own vanishing point, so the arrow and the cones assert the same line.
   */
  const corridorHole = place(gp, axis, S.slalomStart + S.slalomTargetAhead, 0, rng, { hasWater: true });
  place(gp, axis, 108, S.laneOffset * 0.6, rng, { hasWater: true });
  place(gp, axis, 140, -S.laneOffset, rng);
  placeCured(gp, axis, -22, S.laneOffset, rng, 1);

  // The chain is live and banked against the next repair — that is the whole point of gates.
  gp.score.slalomChain = 7;
  gp.score.bestSlalom = 7;
  gp.slalomChainTimer = 0;
  gp.cones.markDirty();
  gp.retargetNow();
  if (corridorHole) gp.forceTarget(corridorHole);
  gp.announce('SLALOM ×7', 'slalom', 1800);
}

/** Elevated establishing shot: holes and cones spread across the whole block. */
function establish(gp, rng, axis, S, T) {
  const span = S.establishSpan;
  let along = -span * 0.35;
  let i = 0;
  while (along < span * 0.65 && gp.potholes.count < T.pothole.capacity - 6) {
    const across = (i % 3 === 0 ? -1 : i % 3 === 1 ? 1 : 0) * S.laneOffset * rng.range(0.6, 1.4);
    if (i % 4 === 3) placeCured(gp, axis, along, across, rng, 1);
    else place(gp, axis, along, across, rng);
    along += rng.range(14, 24);
    i++;
  }
  gp.cones.layRun(
    axis.ox + axis.dx * 20 + axis.px * -1.8, axis.oz + axis.dz * 20 + axis.pz * -1.8,
    axis.dx, axis.dz, 6, rng
  );
  gp.retargetNow();
}

/** RESULT screen backdrop: a street that has actually been fixed. */
function resultTableau(gp, rng, axis, S, T) {
  let along = -20;
  for (let i = 0; i < 9; i++) {
    placeCured(gp, axis, along, (i % 2 === 0 ? -1 : 1) * S.laneOffset * rng.range(0.6, 1.2), rng, 1);
    along += rng.range(16, 26);
  }
  place(gp, axis, 118, S.laneOffset, rng);
  place(gp, axis, 142, -S.laneOffset, rng);
  gp.retargetNow();
}

/** Night rain: every hole is a puddle. */
function nightStreet(gp, rng, axis, S, T) {
  let along = S.nearClear + 2;
  for (let i = 0; i < 8; i++) {
    place(gp, axis, along, (i % 2 === 0 ? -1 : 1) * S.laneOffset * rng.range(0.7, 1.3), rng, { hasWater: true });
    along += rng.range(S.strideMin * 0.8, S.strideMax * 0.85);
  }
  placeCured(gp, axis, -14, S.laneOffset, rng, 0.5);
  gp.cones.layRun(
    axis.ox + axis.dx * 40 + axis.px * 1.5, axis.oz + axis.dz * 40 + axis.pz * 1.5,
    axis.dx, axis.dz, 4, rng
  );
  gp.retargetNow();
}

export { LANDMARKS };
